import { useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ArrowUpRight, Check, Clock, CreditCard, FileText, Nfc, ShieldCheck } from 'lucide-react';
import Payments from '../components/Payments';
import SEO from '../components/SEO';
import BookButton from '../components/BookButton';
import PageHeader from '../components/PageHeader';
import { MaskedLinesOnView, ParallaxImage, Reveal } from '../components/Reveal';
import { JANE_URL, SERVICES } from '../data/site';

function ServiceSection({ service, flip }) {
  return (
    <article
      id={service.slug}
      data-testid={`service-section-${service.slug}`}
      className="scroll-mt-28 border-t border-brand-ink/10 py-16 md:py-24"
    >
      <div className="mx-auto grid max-w-7xl grid-cols-1 items-center gap-12 px-5 md:px-10 lg:grid-cols-12">
        <div className={`lg:col-span-5 ${flip ? 'lg:order-2' : ''}`}>
          <ParallaxImage
            src={service.image}
            alt={service.name}
            className="aspect-[4/5] w-full rounded-b-[2rem] rounded-t-[999px]"
            strength={30}
          />
        </div>
        <div className={`lg:col-span-7 ${flip ? 'lg:order-1 lg:pr-10' : 'lg:pl-10'}`}>
          <Reveal>
            <p className="font-display text-lg italic text-brand-teal/70">{service.num}</p>
            <h2 className="mt-2 font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl">
              {service.name}
            </h2>
          </Reveal>
          {service.body.map((para, i) => (
            <Reveal key={i} delay={0.1 + i * 0.08}>
              <p className="mt-5 max-w-xl text-base leading-relaxed text-brand-moss">{para}</p>
            </Reveal>
          ))}
          <Reveal delay={0.3}>
            <ul className="mt-8 grid grid-cols-1 gap-3 sm:grid-cols-2">
              {service.benefits.map((b) => (
                <li key={b} className="flex items-start gap-3 text-sm font-medium text-brand-ink">
                  <span className="mt-0.5 inline-flex size-5 shrink-0 items-center justify-center rounded-full bg-brand-sage/50 text-brand-teal">
                    <Check className="size-3" strokeWidth={3} />
                  </span>
                  {b}
                </li>
              ))}
            </ul>
          </Reveal>
          <Reveal delay={0.4}>
            <div className="mt-10">
              <BookButton label="Book this session" testId={`service-book-${service.slug}`} />
            </div>
          </Reveal>
        </div>
      </div>
    </article>
  );
}

export default function MassageServices() {
  const { hash } = useLocation();
  useEffect(() => {
    if (hash) {
      const el = document.getElementById(hash.replace('#', ''));
      if (el) setTimeout(() => el.scrollIntoView({ behavior: 'smooth', block: 'start' }), 300);
    }
  }, [hash]);

  const real = SERVICES.filter((s) => !s.comingSoon);
  const soon = SERVICES.find((s) => s.comingSoon);

  return (
    <>
      <SEO
        title="Massage Services | Prenatal, Deep Tissue & Lymphatic — Thula Doula Wellness"
        description="Prenatal & postpartum massage, deep tissue, lymphatic drainage, and therapeutic medical massage in Humble, TX. Body sculpting coming soon. Book online."
      />
      <PageHeader
        testId="services-page-header"
        eyebrow="Massage Services"
        lines={['Therapeutic care,', 'every stage of life.']}
        sub="Every session begins with listening — then pressure, positioning, and focus are tailored to what your body actually needs."
      />

      <section className="bg-brand-cream pb-8 pt-4 md:pb-12">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <p className="flex items-center gap-3 text-sm font-medium text-brand-moss" data-testid="services-session-note">
              <Clock className="size-4 text-brand-teal" />
              Sessions are booked in 60- and 90-minute appointments. We accept HSA &amp; FSA cards.
              Live availability and pricing
              are on our{' '}
              <a
                href={JANE_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1 font-semibold text-brand-teal underline decoration-brand-sage underline-offset-4"
              >
                Jane booking page <ArrowUpRight className="size-3.5" />
              </a>
              .
            </p>
          </Reveal>
        </div>
      </section>

      <section className="bg-brand-cream pb-4 md:pb-8" data-testid="payments-massage">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Payments
            testId="payments-massage"
            title="Simple, compliant payments."
            methods={[
              { icon: CreditCard, label: 'All major credit & debit cards' },
              { icon: FileText, label: 'HSA & FSA cards accepted' },
              { icon: ShieldCheck, label: 'VA Community Care billing for authorized visits' },
              { icon: Nfc, label: 'Cashless practice — card & digital payments only' },
            ]}
            notes={[
              'Authorized VA Community Care visits are billed to the VA — nothing out of pocket for covered care.',
              'HSA and FSA cards run like any other card, and we provide itemized receipts for your administrator or reimbursement claims.',
            ]}
          />
        </div>
      </section>

      <div className="bg-brand-cream">
        {real.map((s, i) => (
          <ServiceSection key={s.slug} service={s} flip={i % 2 === 1} />
        ))}
      </div>

      <section className="bg-brand-cream pb-24 md:pb-32" data-testid="body-sculpting-section">
        <div className="mx-auto max-w-7xl px-5 md:px-10">
          <Reveal>
            <div
              data-testid="body-sculpting-card"
              className="relative overflow-hidden rounded-3xl border border-brand-teal/15 bg-brand-mist/40 p-8 md:p-14"
            >
              <div
                aria-hidden="true"
                className="pointer-events-none absolute -right-20 -top-20 size-64 rounded-full bg-brand-sage/40 blur-3xl"
              />
              <span
                data-testid="body-sculpting-pill"
                className="inline-block rounded-full bg-brand-teal px-4 py-1.5 text-[10px] font-bold uppercase tracking-[0.2em] text-brand-cream"
              >
                Coming soon
              </span>
              <MaskedLinesOnView
                as="h2"
                lines={['Body Sculpting', 'is on the way.']}
                className="mt-6 font-display text-4xl font-light tracking-tight text-brand-ink md:text-5xl"
              />
              <p className="mt-5 max-w-xl text-base leading-relaxed text-brand-moss">
                Non-invasive body sculpting treatments are joining the studio later this year. We’re
                building the treatment space now — call or email to join the interest list and be
                first to know when scheduling opens.
              </p>
              <div className="mt-8 flex flex-wrap gap-4">
                <a
                  data-testid="body-sculpting-interest-btn"
                  href="mailto:Info@thuladoulamassage.com?subject=Body%20Sculpting%20interest%20list"
                  className="group inline-flex items-center gap-2.5 rounded-full bg-brand-teal px-7 py-3.5 text-sm font-semibold text-brand-cream transition-[background-color,transform] duration-300 hover:bg-brand-ink active:scale-[0.97]"
                >
                  Join the interest list
                  <ArrowUpRight className="size-4 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </a>
              </div>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
